import { useMemo } from "react";
import { FileDown, FileText } from "lucide-react";

import { formatNumber } from "@/utils/numberFormat";
import { formatDate } from "@/utils/dateFormatter";
import { formatNameWithTooltip } from "@/utils/nameFormatter";
import { formatUser } from "@/utils/userFormatter";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

import { getMovementTypeLabel, getMovementTypeVariant } from "@/constants/movementTypes";

export function useOutputHistoryColumns(tableState, { onAssignOA, onExportPDF }) {
	return useMemo(() => {
		const columns = [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: "3%",
				Cell: ({ row }) => {
					return <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>;
				},
			},
			{
				Header: "Fecha Registro",
				accessor: "created_date",
				width: "8%",
				Cell: ({ value }) => formatDate(value),
			},
			{
				Header: "Origen",
				accessor: "estate_parent_origin_name",
				width: "12%",
				Cell: ({ value, row }) => {
					const originName = row.original.estate_origin_name || "";
					const parentOriginName = value || "";

					if (originName === parentOriginName) {
						const { abbreviated, full } = formatNameWithTooltip(originName);
						return <CustomTooltip text={abbreviated} description={full} />;
					}

					const displayText = parentOriginName && originName ? `${parentOriginName} - ${originName}` : parentOriginName || originName;
					const { abbreviated, full } = formatNameWithTooltip(displayText);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "Destino",
				accessor: "estate_parent_name",
				width: "12%",
				Cell: ({ value, row }) => {
					const estateName = row.original.estate_name || "";
					const parentName = value || "";

					if (estateName === parentName) {
						const { abbreviated, full } = formatNameWithTooltip(estateName);
						return <CustomTooltip text={abbreviated} description={full} />;
					}

					const displayText = parentName && estateName ? `${parentName} - ${estateName}` : parentName || estateName;
					const { abbreviated, full } = formatNameWithTooltip(displayText);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "Centro Costo",
				accessor: "business_center_name",
				width: "11%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const centers = value.split(",").map((center) => center.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{centers.map((center, index) => (
								<CustomBadge key={index} variant="neutral" size="sm">
									{center}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Cant. Envases",
				accessor: "quantity",
				width: "7%",
				meta: { cellClassName: "tdRight" },
				Cell: ({ value }) => {
					return <span style={{ fontWeight: 500 }}>{formatNumber(value)}</span>;
				},
			},
			{
				Header: "Usuario",
				accessor: "user_name",
				width: "10%",
				Cell: ({ value }) => {
					const { abbreviated, full } = formatUser(value);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "Operador",
				accessor: "operator_name",
				width: "11%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const userNames = value.split(",").map((userName) => userName.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{userNames.map((userName, index) => {
								const { abbreviated } = formatUser(userName);
								return (
									<CustomBadge key={index} variant="neutral" size="sm">
										{abbreviated}
									</CustomBadge>
								);
							})}
						</div>
					);
				},
			},
			{
				Header: "N° OA",
				accessor: "application_order_code",
				width: "8%",
				Cell: ({ value, row }) => {
					if (!value) return <span>-</span>;

					const codes = value.split(",").map((code) => code.trim());
					const typeOADescription = row.original.type_movement_oa;

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{codes.map((code, index) => (
								<CustomBadge key={index} variant="neutral" size="sm">
									{typeOADescription ? `${code} - ${typeOADescription}` : code}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Tipo",
				accessor: "type_movement",
				width: "10%",
				meta: { cellClassName: "tdCenter" },
				Cell: ({ value }) => {
					const label = getMovementTypeLabel(value);
					const variant = getMovementTypeVariant(value);

					return (
						<CustomBadge variant={variant} size="sm">
							{label}
						</CustomBadge>
					);
				},
			},
			{
				Header: "",
				accessor: "_expand",
				disableSortBy: true,
				width: "0%",
				Cell: () => null,
			},
		];

		if (onAssignOA || onExportPDF) {
			columns.splice(-1, 0, {
				Header: "",
				accessor: "applications_ids",
				disableSortBy: true,
				width: "3%",
				Cell: ({ row }) => {
					const items = [];

					if (onAssignOA) {
						items.push({
							label: "Orden de Aplicación",
							action: () => onAssignOA(row.original.output_code, row.original.balance_id),
							icon: FileText,
						});
					}

					if (onExportPDF) {
						items.push({
							label: "Descargar PDF",
							action: () => onExportPDF(row.original),
							icon: FileDown,
						});
					}

					return (
						<div style={{ textAlign: "center" }}>
							<CustomDropdown title="Acciones" items={items} align="right" />
						</div>
					);
				},
			});
		}

		return columns;
	}, [tableState, onAssignOA, onExportPDF]);
}
